import { useEffect, useRef } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSequence,
  withTiming,
  Easing,
} from "react-native-reanimated";

export type PulseProps = {
  /** Change this value to trigger a pulse. */
  trigger: unknown;
  /** Peak scale. Default 1.05. */
  intensity?: number;
  /** Total pulse duration in ms. Default 300. */
  duration?: number;
  style?: StyleProp<ViewStyle>;
  children: React.ReactNode;
};

export function Pulse({
  trigger,
  intensity = 1.05,
  duration = 300,
  style,
  children,
}: PulseProps) {
  const scale = useSharedValue(1);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    scale.value = withSequence(
      withTiming(intensity, { duration: duration * 0.35, easing: Easing.out(Easing.quad) }),
      withTiming(1, { duration: duration * 0.65, easing: Easing.inOut(Easing.quad) }),
    );
  }, [trigger]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  return (
    <Animated.View style={[animatedStyle, style]}>
      {children}
    </Animated.View>
  );
}
